import React, { useState, useEffect } from 'react';
import { normalizeTimestamp, isMilliseconds, formatSafeDate, secondsToDuration, getCodeSnippets } from '../utils/timeUtils';
import { ArrowRightLeft, Clock, Code, Info } from 'lucide-react';
import { startOfYear, endOfYear, startOfMonth, startOfDay } from 'date-fns';

const Home: React.FC = () => {
  // Converter State
  const [inputTs, setInputTs] = useState<string>(Math.floor(Date.now() / 1000).toString());
  const [convertedDate, setConvertedDate] = useState<string>('');
  const [convertedRelative, setConvertedRelative] = useState<string>('');
  
  const [inputDate, setInputDate] = useState<string>(new Date().toISOString().slice(0, 16));
  const [convertedTs, setConvertedTs] = useState<number>(0);

  const [durationInput, setDurationInput] = useState<string>('86400');
  const [durationOutput, setDurationOutput] = useState<string>('');

  useEffect(() => {
    // Timestamp -> Date
    const ts = parseInt(inputTs);
    if (!isNaN(ts)) {
      const isMs = isMilliseconds(ts);
      const date = new Date(isMs ? ts : ts * 1000);
      setConvertedDate(formatSafeDate(date, 'yyyy-MM-dd HH:mm:ss'));
      setConvertedRelative(isMs ? '(Milliseconds detected)' : '(Seconds detected)');
    } else {
      setConvertedDate('Invalid Timestamp');
      setConvertedRelative('');
    }
  }, [inputTs]);

  useEffect(() => {
    // Date -> Timestamp
    const date = new Date(inputDate);
    if (!isNaN(date.getTime())) {
      setConvertedTs(Math.floor(date.getTime() / 1000));
    } else {
      setConvertedTs(0);
    }
  }, [inputDate]);

  useEffect(() => {
    // Seconds -> Duration
    const sec = parseInt(durationInput);
    if (!isNaN(sec)) {
      setDurationOutput(secondsToDuration(sec));
    } else {
      setDurationOutput('Invalid input');
    }
  }, [durationInput]);

  const quickRefs = [
    { label: 'Start of Year', ts: Math.floor(startOfYear(new Date()).getTime() / 1000) },
    { label: 'End of Year', ts: Math.floor(endOfYear(new Date()).getTime() / 1000) },
    { label: 'Start of Month', ts: Math.floor(startOfMonth(new Date()).getTime() / 1000) },
    { label: 'Today (Start)', ts: Math.floor(startOfDay(new Date()).getTime() / 1000) },
  ];

  const snippets = getCodeSnippets(Math.floor(Date.now() / 1000));

  return (
    <div className="space-y-8 animate-fade-in">
      
      {/* Converters Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* TS -> Date */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Clock size={20} className="text-sky-500" /> Timestamp to Date
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Epoch Timestamp</label>
              <input 
                type="number" 
                value={inputTs}
                onChange={(e) => setInputTs(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none font-mono text-slate-800"
                placeholder="e.g. 1672531200"
              />
              <p className="text-xs text-slate-500 mt-1">{convertedRelative}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
              <span className="block text-xs uppercase text-slate-400 font-semibold mb-1">Local Time</span>
              <div className="text-xl font-mono text-slate-800 break-all">{convertedDate}</div>
              <span className="block text-xs uppercase text-slate-400 font-semibold mt-3 mb-1">GMT / UTC</span>
              <div className="text-lg font-mono text-slate-600 break-all">
                {!isNaN(parseInt(inputTs)) ? new Date(parseInt(inputTs) * (isMilliseconds(parseInt(inputTs)) ? 1 : 1000)).toUTCString() : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Date -> TS */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <ArrowRightLeft size={20} className="text-sky-500" /> Date to Timestamp
          </h2>
          <div className="space-y-4">
             <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Date & Time</label>
              <input 
                type="datetime-local" 
                value={inputDate}
                onChange={(e) => setInputDate(e.target.value)}
                className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none font-mono text-slate-800"
              />
            </div>
             <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
              <span className="block text-xs uppercase text-slate-400 font-semibold mb-1">Epoch Timestamp</span>
              <div className="text-2xl font-mono text-sky-600 font-bold">{convertedTs}</div>
              <span className="block text-xs uppercase text-slate-400 font-semibold mt-3 mb-1">Milliseconds</span>
              <div className="text-lg font-mono text-slate-600">{convertedTs * 1000}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Seconds to Duration & Quick Ref */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        <div className="lg:col-span-1 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
           <h2 className="text-lg font-bold text-slate-800 mb-4">Seconds to Human Readable</h2>
           <input 
              type="number" 
              value={durationInput}
              onChange={(e) => setDurationInput(e.target.value)}
              className="w-full px-4 py-2 mb-4 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none font-mono text-slate-800"
              placeholder="Total seconds"
            />
            <div className="p-3 bg-sky-50 text-sky-900 rounded-md font-medium text-center">
              {durationOutput}
            </div>
        </div>

        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Quick Reference (Current Year)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Event</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Epoch</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {quickRefs.map((ref, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-3 py-3 text-sm font-medium text-slate-900">{ref.label}</td>
                    <td className="px-3 py-3 text-sm font-mono text-sky-600">{ref.ts}</td>
                    <td className="px-3 py-3 text-sm text-slate-500">{new Date(ref.ts * 1000).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Info & Code Snippets */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h2 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <Info size={24} className="text-sky-500" /> What is Epoch Time?
        </h2>
        <div className="prose text-slate-600 mb-8 max-w-none">
          <p>
            The <strong>Unix epoch</strong> (or Unix time, POSIX time, or Unix timestamp) is the number of seconds that have elapsed since 
            <strong> January 1, 1970 (midnight UTC/GMT)</strong>, not counting leap seconds (in ISO 8601: 1970-01-01T00:00:00Z).
            Literally speaking, the epoch is Unix time 0 (midnight 1/1/1970), but 'epoch' is often used as a synonym for Unix time.
            Some systems store epoch times as a signed 32-bit integer, which will cause the <span className="font-mono text-red-500">Year 2038 problem</span> 
            on January 19, 2038.
          </p>
        </div>

        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Code size={20} className="text-sky-500" /> How to get the current epoch
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {snippets.map((s) => (
            <div key={s.lang} className="bg-slate-900 rounded-lg p-4 overflow-hidden">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-slate-400 uppercase">{s.lang}</span>
                <button 
                  onClick={() => navigator.clipboard.writeText(s.code)}
                  className="text-slate-500 hover:text-white transition-colors"
                  title="Copy"
                >
                  <Code size={14}/>
                </button>
              </div>
              <code className="block font-mono text-sm text-sky-300 whitespace-pre-wrap">{s.code}</code>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default Home;